// @ts-nocheck
import * as React from "react";
import { useLoaderData } from "react-router-dom";
import { useForm, FormProvider, useWatch } from "react-hook-form";

import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Grid from "@mui/material/Unstable_Grid2";
import Divider from "@mui/material/Divider";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Slider from "@mui/material/Slider";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { Radar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from "chart.js";

import Header from "../components/Header";
import Form from "../components/form/Form";
import FormDropDownFilter from "../components/form/FormDropDownFilter";
import FormRadioGroup from "../components/form/FormRadioGroup";
import FormMultiSelectChips from "../components/form/FormMultiSelectChips";
import FormButton from "../components/form/FormButton";
import { listAnimals } from "../models/animals";
import { getAssessment, listAssessments } from "../models/assessments";
import { listSectionAsByAssessment } from "../models/sectiona";
import { listSectionBsByAssessment } from "../models/sectionb";
import { listUsers } from "../models/users";
import { date } from "../utils/datetime";
import { name as animalName } from "../utils/animal";
import { name as speciesName } from "../utils/species";
import { useMessage } from "../context/MessageProvider";
import { sectionBBehaviors } from "../utils/sectionBBehaviors";
import {
  averageAdjectiveComposite,
  sectionBBehaviorFrequencyPercents,
} from "../utils/sections";

ChartJS.register(RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

export async function loader() {
  const animals = await listAnimals();
  const assessments = await listAssessments();
  const users = await listUsers();
  return { animals, assessments, users };
}

const SECTION_A_SCORE_FIELDS = [
  "tense",
  "relaxed",
  "lively",
  "withdrawn",
  "curious",
  "at_ease",
  "fearful",
  "wary",
  "content",
  "irritable",
  "frustrated",
  "sociable",
  "indifferent",
  "comfortable",
  "calm",
  "playful",
  "agitated",
  "listless",
  "anxious",
  "excited",
];
const DEFAULT_RADAR_HEIGHT = 640;
const MIN_RADAR_HEIGHT = 320;
const MAX_RADAR_HEIGHT = 1400;
const RADAR_HEIGHT_STEP = 40;
const SECTION_A_DATA_MODE_RAW = "raw_qba_scores";
const SECTION_A_DATA_MODE_COMPOSITE = "qba_composite_scores";
const SECTION_B_DATA_MODE_RAW = "raw_counts";
const SECTION_B_DATA_MODE_PERCENT = "frequency_percentages";
const OBSERVATION_AGGREGATION_RANGE = "show_each_observation_range";
const OBSERVATION_AGGREGATION_ROLLUP = "rollup_all_observation_ranges";
const SECTION_A_DATA_MODE_OPTIONS = [
  { value: SECTION_A_DATA_MODE_RAW, label: "Raw QBA Scores" },
  { value: SECTION_A_DATA_MODE_COMPOSITE, label: "QBA Average Scores" },
];
const SECTION_B_DATA_MODE_OPTIONS = [
  { value: SECTION_B_DATA_MODE_RAW, label: "Raw Frequency Counts" },
  { value: SECTION_B_DATA_MODE_PERCENT, label: "Frequency Percentages" },
];
const OBSERVATION_AGGREGATION_OPTIONS = [
  { value: OBSERVATION_AGGREGATION_RANGE, label: "Show Each Observation Range" },
  { value: OBSERVATION_AGGREGATION_ROLLUP, label: "Rollup All Observation Ranges" },
];
const QBA_COMPOSITE_DEFINITIONS = [
  {
    key: "positive_calm",
    label: "Positive Calm",
    getValue: (sectionA) => averageAdjectiveComposite(
      sectionA ? [sectionA] : [],
      ["content", "calm", "comfortable", "at_ease", "relaxed"]
    ),
  },
  {
    key: "negative_calm",
    label: "Negative Calm",
    getValue: (sectionA) => averageAdjectiveComposite(
      sectionA ? [sectionA] : [],
      ["withdrawn", "tense", "listless", "wary", "indifferent"]
    ),
  },
  {
    key: "positive_active",
    label: "Positive Active",
    getValue: (sectionA) => averageAdjectiveComposite(
      sectionA ? [sectionA] : [],
      ["lively", "playful", "sociable", "curious", "excited"]
    ),
  },
  {
    key: "negative_active",
    label: "Negative Active",
    getValue: (sectionA) => averageAdjectiveComposite(
      sectionA ? [sectionA] : [],
      ["frustrated", "irritable", "agitated", "anxious", "fearful"]
    ),
  },
];

const labelFromKey = (key) => key
  .split("_")
  .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
  .join(" ");

const sectionBBehaviorValueMap = (sectionB, dataMode) => {
  if (dataMode === SECTION_B_DATA_MODE_PERCENT) {
    const frequencyData = sectionBBehaviorFrequencyPercents(sectionB ? [sectionB] : []);
    return sectionBBehaviors.reduce((acc, behaviorDefinition) => {
      acc[behaviorDefinition.behaviorKey] = (
        frequencyData.behaviors?.[behaviorDefinition.behaviorKey]?.percent || 0
      );
      return acc;
    }, {});
  }

  const counts = (sectionB?.behaviors || []).reduce((acc, behaviorRecord) => {
    const key = behaviorRecord?.behavior;
    if (!key) return acc;
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  return sectionBBehaviors.reduce((acc, behaviorDefinition) => {
    acc[behaviorDefinition.behaviorKey] = counts[behaviorDefinition.behaviorKey] || 0;
    return acc;
  }, {});
};

const formatSectionBValue = (value, dataMode) => {
  if (dataMode === SECTION_B_DATA_MODE_PERCENT) return value.toFixed(1);
  return String(value);
};

const sectionAValueDefinitions = (dataMode) => {
  if (dataMode === SECTION_A_DATA_MODE_COMPOSITE) {
    return QBA_COMPOSITE_DEFINITIONS.map((definition) => ({
      key: definition.key,
      label: definition.label,
      getValue: (sectionA) => definition.getValue(sectionA),
    }));
  }

  return SECTION_A_SCORE_FIELDS.map((field) => ({
    key: field,
    label: labelFromKey(field),
    getValue: (sectionA) => Number(sectionA?.[field] || 0),
  }));
};

const averageNumber = (values = []) => {
  if (!values.length) return 0;
  return values.reduce((acc, value) => acc + value, 0) / values.length;
};

const rollupSectionA = (sectionAs = [], assessmentId) => {
  const rolled = SECTION_A_SCORE_FIELDS.reduce((acc, field) => {
    acc[field] = averageNumber(
      (sectionAs || []).map((sectionA) => Number(sectionA?.[field] || 0))
    );
    return acc;
  }, {});

  return {
    id: `section-a-summary-${assessmentId}`,
    assessment_id: assessmentId,
    observed_on: null,
    ...rolled,
  };
};

const rollupSectionBValues = (sectionBs = [], dataMode) => {
  if (!sectionBs.length) {
    return sectionBBehaviors.reduce((acc, behaviorDefinition) => {
      acc[behaviorDefinition.behaviorKey] = 0;
      return acc;
    }, {});
  }

  const perRangeValues = sectionBs.map((sectionB) => sectionBBehaviorValueMap(sectionB, dataMode));
  return sectionBBehaviors.reduce((acc, behaviorDefinition) => {
    const key = behaviorDefinition.behaviorKey;
    acc[key] = averageNumber(perRangeValues.map((valueMap) => valueMap[key] || 0));
    return acc;
  }, {});
};

const assessmentSeriesLabel = (assessment, fallbackId) => (
  assessment?.assessment_title || `Assessment ${assessment?.id || fallbackId}`
);

const ChartSeriesControls = ({ onActivateAll, onDeactivateAll }) => (
  <Stack direction="row" spacing={1} justifyContent="center" sx={{ mb: 2 }}>
    <Button size="small" variant="outlined" onClick={onActivateAll}>
      Activate all series
    </Button>
    <Button size="small" variant="outlined" onClick={onDeactivateAll}>
      Deactivate all series
    </Button>
  </Stack>
);

const average = (values = []) => {
  if (!values.length) return 0;
  const sum = values.reduce((acc, value) => acc + value, 0);
  return sum / values.length;
};

const median = (values = []) => {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 0) {
    return (sorted[mid - 1] + sorted[mid]) / 2;
  }
  return sorted[mid];
};

const standardDeviation = (values = []) => {
  if (!values.length) return 0;
  const mean = average(values);
  const variance = values.reduce((acc, value) => {
    const diff = value - mean;
    return acc + (diff * diff);
  }, 0) / values.length;
  return Math.sqrt(variance);
};

const calculateStats = (values = []) => {
  const avg = average(values);
  const med = median(values);
  const stdDev = standardDeviation(values);
  return { avg, med, stdDev };
};

const deviationStyle = (value, avg, stdDev) => {
  if (!Number.isFinite(stdDev) || stdDev <= 0) return null;
  const zScore = Math.abs((value - avg) / stdDev);
  if (zScore > 2) return { bgcolor: "error.light" };
  if (zScore >= 1) return { color: "warning.light", fontWeight: 600 };
  return null;
};

const CHART_COLORS = [
  "rgb(31, 119, 180)",
  "rgb(255, 127, 14)",
  "rgb(44, 160, 44)",
  "rgb(214, 39, 40)",
  "rgb(148, 103, 189)",
  "rgb(140, 86, 75)",
  "rgb(227, 119, 194)",
  "rgb(127, 127, 127)",
  "rgb(188, 189, 34)",
  "rgb(23, 190, 207)",
  "rgb(57, 106, 177)",
  "rgb(218, 124, 48)",
  "rgb(62, 150, 81)",
  "rgb(204, 37, 41)",
  "rgb(83, 81, 84)",
  "rgb(107, 76, 154)",
  "rgb(146, 36, 40)",
  "rgb(148, 139, 61)",
  "rgb(22, 118, 191)",
  "rgb(153, 153, 153)",
];

const toAlpha = (rgbValue, alpha) => rgbValue.replace("rgb", "rgba").replace(")", `, ${alpha})`);

const hashColorIndex = (assessmentId) => {
  const key = String(assessmentId || "");
  if (!key) return 0;
  const hash = key.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return hash % CHART_COLORS.length;
};

const rgbParts = (rgbValue) => {
  const matches = String(rgbValue).match(/\d+/g) || [];
  return matches.slice(0, 3).map((part) => Number(part));
};

const colorDistance = (leftColor, rightColor) => {
  const [lr, lg, lb] = rgbParts(leftColor);
  const [rr, rg, rb] = rgbParts(rightColor);
  const dr = lr - rr;
  const dg = lg - rg;
  const db = lb - rb;
  return Math.sqrt((dr * dr) + (dg * dg) + (db * db));
};

const assignDistinctAssessmentColors = (assessmentIds = []) => {
  const uniqueIds = Array.from(new Set(
    (assessmentIds || []).filter(Boolean).map((id) => String(id))
  )).sort((a, b) => a.localeCompare(b));

  const minDistance = 110;
  const chosenColors = [];
  const map = {};

  uniqueIds.forEach((assessmentId) => {
    const startIndex = hashColorIndex(assessmentId);
    let selectedColor = null;

    for (let offset = 0; offset < CHART_COLORS.length; offset += 1) {
      const candidate = CHART_COLORS[(startIndex + offset) % CHART_COLORS.length];
      const isDistinctEnough = chosenColors.every((usedColor) => (
        colorDistance(candidate, usedColor) >= minDistance
      ));
      if (isDistinctEnough) {
        selectedColor = candidate;
        break;
      }
    }

    if (!selectedColor) {
      let bestCandidate = CHART_COLORS[startIndex % CHART_COLORS.length];
      let bestDistance = -1;
      CHART_COLORS.forEach((candidate) => {
        const distanceToClosest = chosenColors.length
          ? Math.min(...chosenColors.map((usedColor) => colorDistance(candidate, usedColor)))
          : Number.POSITIVE_INFINITY;
        if (distanceToClosest > bestDistance) {
          bestDistance = distanceToClosest;
          bestCandidate = candidate;
        }
      });
      selectedColor = bestCandidate;
    }

    chosenColors.push(selectedColor);
    map[assessmentId] = selectedColor;
  });

  return map;
};

export default function AssessmentComparisonPage({ title = "Assessment Comparison" }) {
  const { animals, assessments, users } = useLoaderData();
  const { setSuccess, setError } = useMessage();
  const [working, setWorking] = React.useState(false);
  const [sectionAByAssessmentData, setSectionAByAssessmentData] = React.useState([]);
  const [sectionBByAssessmentData, setSectionBByAssessmentData] = React.useState([]);
  const [sectionAComparisons, setSectionAComparisons] = React.useState([]);
  const [sectionBComparisons, setSectionBComparisons] = React.useState([]);
  const [sectionAOpen, setSectionAOpen] = React.useState(true);
  const [sectionBOpen, setSectionBOpen] = React.useState(true);
  const [sectionARangeOpen, setSectionARangeOpen] = React.useState({});
  const [sectionBRangeOpen, setSectionBRangeOpen] = React.useState({});
  const [sectionARangeChartHeight, setSectionARangeChartHeight] = React.useState({});
  const [sectionBRangeChartHeight, setSectionBRangeChartHeight] = React.useState({});
  const [sectionARadarSeriesVisibility, setSectionARadarSeriesVisibility] = React.useState({});
  const [sectionBRadarSeriesVisibility, setSectionBRadarSeriesVisibility] = React.useState({});

  const methods = useForm({
    defaultValues: {
      animal_id: "",
      assessment_ids: [],
      section_a_data_mode: SECTION_A_DATA_MODE_RAW,
      section_b_data_mode: SECTION_B_DATA_MODE_RAW,
      observation_aggregation: OBSERVATION_AGGREGATION_RANGE,
    },
  });
  const { control, setValue, handleSubmit } = methods;
  const selectedAnimalId = useWatch({ control, name: "animal_id" });
  const selectedSectionADataMode = useWatch({
    control,
    name: "section_a_data_mode",
    defaultValue: SECTION_A_DATA_MODE_RAW,
  });
  const selectedSectionBDataMode = useWatch({
    control,
    name: "section_b_data_mode",
    defaultValue: SECTION_B_DATA_MODE_RAW,
  });
  const selectedObservationAggregation = useWatch({
    control,
    name: "observation_aggregation",
    defaultValue: OBSERVATION_AGGREGATION_RANGE,
  });
  const isRollupAggregation = selectedObservationAggregation === OBSERVATION_AGGREGATION_ROLLUP;

  const speciesFilter = animals.reduce((acc, cur) => {
      if (acc.includes(cur.species) === false) acc.push(cur.species);
      return acc
  }, []).map((cur) => {
      return { value: cur, label: speciesName(cur) };
  });

  const animalOptions = React.useMemo(() => (animals || [])
    .map((animal) => ({ value: animal.id, label: animalName(animal) }))
    .sort((a, b) => String(a.label || "").localeCompare(String(b.label || ""))), [animals]);
  const assessmentsById = React.useMemo(() => new Map(
    (assessments || []).map((assessment) => [String(assessment.id), assessment])
  ), [assessments]);
  const usersById = React.useMemo(() => new Map(
    (users || []).map((user) => [String(user.id), user])
  ), [users]);
  const comparedAssessmentIds = React.useMemo(() => {
    const ids = [];
    sectionAComparisons.forEach((rangeGroup) => {
      (rangeGroup.rows || []).forEach(({ assessment, sectionA }) => {
        ids.push(assessment?.id || sectionA?.assessment_id);
      });
    });
    sectionBComparisons.forEach((rangeGroup) => {
      (rangeGroup.rows || []).forEach(({ assessment, sectionB }) => {
        ids.push(assessment?.id || sectionB?.assessment_id);
      });
    });
    return ids;
  }, [sectionAComparisons, sectionBComparisons]);
  const assessmentColors = React.useMemo(
    () => assignDistinctAssessmentColors(comparedAssessmentIds),
    [comparedAssessmentIds]
  );
  const assessmentColor = React.useCallback((assessmentId) => (
    assessmentColors[String(assessmentId)] || CHART_COLORS[hashColorIndex(assessmentId)]
  ), [assessmentColors]);

  const assessmentOptions = React.useMemo(() => {
    if (!selectedAnimalId) return [];
    return (assessments || [])
      .filter((assessment) => String(assessment.animal_id) === String(selectedAnimalId))
      .sort((a, b) => {
        const aStarted = a.started_on?.valueOf ? a.started_on.valueOf() : 0;
        const bStarted = b.started_on?.valueOf ? b.started_on.valueOf() : 0;
        return bStarted - aStarted;
      })
      .map((assessment) => {
        const startedText = assessment.started_on ? date(assessment.started_on) : "Unknown start date";
        const submittedText = assessment.submitted_on ? date(assessment.submitted_on) : "Not submitted";

        const defaultTitle = `Started ${startedText}`;
        const label = ((assessment.assessment_title) ? assessment.assessment_title : defaultTitle) + `, Submitted ${submittedText}`;

        return {
          value: assessment.id,
          label: label,
        };
      });
  }, [assessments, selectedAnimalId]);

  React.useEffect(() => {
    setValue("assessment_ids", []);
    setSectionAByAssessmentData([]);
    setSectionBByAssessmentData([]);
    setSectionAComparisons([]);
    setSectionBComparisons([]);
    setSectionARangeOpen({});
    setSectionBRangeOpen({});
    setSectionARangeChartHeight({});
    setSectionBRangeChartHeight({});
    setSectionARadarSeriesVisibility({});
    setSectionBRadarSeriesVisibility({});
  }, [selectedAnimalId, setValue]);

  const onSubmit = async (inputData) => {
    setWorking(true);
    try {
      const selectedIds = (inputData?.assessment_ids || []).filter(Boolean);
      if (!selectedIds.length) {
        setSectionAByAssessmentData([]);
        setSectionBByAssessmentData([]);
        setSectionAComparisons([]);
        setSectionBComparisons([]);
        setSectionARangeOpen({});
        setSectionBRangeOpen({});
        setSectionARangeChartHeight({});
        setSectionBRangeChartHeight({});
        setSectionARadarSeriesVisibility({});
        setSectionBRadarSeriesVisibility({});
        return;
      }

      const assessmentRecords = await Promise.all(
        selectedIds.map((assessmentId) => getAssessment(assessmentId))
      );

      const sectionAByAssessment = await Promise.all(
        selectedIds.map(async (assessmentId) => ({
          assessment_id: assessmentId,
          sectionAs: await listSectionAsByAssessment(assessmentId),
        }))
      );
      const sectionBByAssessment = await Promise.all(
        selectedIds.map(async (assessmentId) => ({
          assessment_id: assessmentId,
          sectionBs: await listSectionBsByAssessment(assessmentId),
        }))
      );

      const assessmentMap = new Map(
        assessmentRecords
          .filter(Boolean)
          .map((assessment) => [String(assessment.id), assessment])
      );

      const rangeMap = new Map();
      const rangeOrderMap = new Map();

      sectionAByAssessment.forEach(({ assessment_id: assessmentId, sectionAs }) => {
        (sectionAs || []).forEach((sectionA) => {
          const rangeKey = String(sectionA.observation_range || "Unknown observation range");
          if (!rangeMap.has(rangeKey)) rangeMap.set(rangeKey, []);
          rangeMap.get(rangeKey).push({
            assessment: assessmentMap.get(String(assessmentId)),
            sectionA,
          });

          const sortOrder = Number(sectionA.sort_order);
          const currentMin = rangeOrderMap.get(rangeKey);
          if (!Number.isNaN(sortOrder)) {
            if (currentMin === undefined || sortOrder < currentMin) {
              rangeOrderMap.set(rangeKey, sortOrder);
            }
          }
        });
      });

      const sortedRanges = Array.from(rangeMap.entries())
        .sort((a, b) => {
          const aOrder = rangeOrderMap.get(a[0]);
          const bOrder = rangeOrderMap.get(b[0]);
          if (aOrder === undefined && bOrder === undefined) return a[0].localeCompare(b[0]);
          if (aOrder === undefined) return 1;
          if (bOrder === undefined) return -1;
          if (aOrder === bOrder) return a[0].localeCompare(b[0]);
          return aOrder - bOrder;
        })
        .map(([observationRange, rows]) => ({
          observationRange,
          rows: rows.sort((a, b) => {
            const aDate = a.assessment?.started_on?.valueOf ? a.assessment.started_on.valueOf() : 0;
            const bDate = b.assessment?.started_on?.valueOf ? b.assessment.started_on.valueOf() : 0;
            return aDate - bDate;
          }),
        }));

      const sectionBRangeMap = new Map();
      const sectionBRangeOrderMap = new Map();

      sectionBByAssessment.forEach(({ assessment_id: assessmentId, sectionBs }) => {
        (sectionBs || []).forEach((sectionB) => {
          const rangeKey = String(sectionB.observation_range || "Unknown observation range");
          if (!sectionBRangeMap.has(rangeKey)) sectionBRangeMap.set(rangeKey, []);
          sectionBRangeMap.get(rangeKey).push({
            assessment: assessmentMap.get(String(assessmentId)),
            sectionB,
          });

          const sortOrder = Number(sectionB.sort_order);
          const currentMin = sectionBRangeOrderMap.get(rangeKey);
          if (!Number.isNaN(sortOrder)) {
            if (currentMin === undefined || sortOrder < currentMin) {
              sectionBRangeOrderMap.set(rangeKey, sortOrder);
            }
          }
        });
      });

      const sortedSectionBRanges = Array.from(sectionBRangeMap.entries())
        .sort((a, b) => {
          const aOrder = sectionBRangeOrderMap.get(a[0]);
          const bOrder = sectionBRangeOrderMap.get(b[0]);
          if (aOrder === undefined && bOrder === undefined) return a[0].localeCompare(b[0]);
          if (aOrder === undefined) return 1;
          if (bOrder === undefined) return -1;
          if (aOrder === bOrder) return a[0].localeCompare(b[0]);
          return aOrder - bOrder;
        })
        .map(([observationRange, rows]) => ({
          observationRange,
          rows: rows.sort((a, b) => {
            const aDate = a.assessment?.started_on?.valueOf ? a.assessment.started_on.valueOf() : 0;
            const bDate = b.assessment?.started_on?.valueOf ? b.assessment.started_on.valueOf() : 0;
            return aDate - bDate;
          }),
        }));

      setSectionAByAssessmentData(sectionAByAssessment);
      setSectionBByAssessmentData(sectionBByAssessment);
      setSectionAComparisons(sortedRanges);
      setSectionBComparisons(sortedSectionBRanges);
      setSectionAOpen(true);
      setSectionBOpen(true);
      setSectionARangeOpen(Object.fromEntries(
        sortedRanges.map((rangeGroup) => [rangeGroup.observationRange, true])
      ));
      setSectionBRangeOpen(Object.fromEntries(
        sortedSectionBRanges.map((rangeGroup) => [rangeGroup.observationRange, true])
      ));
      setSectionARangeChartHeight(Object.fromEntries(
        sortedRanges.map((rangeGroup) => [rangeGroup.observationRange, DEFAULT_RADAR_HEIGHT])
      ));
      setSectionBRangeChartHeight(Object.fromEntries(
        sortedSectionBRanges.map((rangeGroup) => [rangeGroup.observationRange, DEFAULT_RADAR_HEIGHT])
      ));
      setSectionARadarSeriesVisibility({});
      setSectionBRadarSeriesVisibility({});
      setSuccess("Comparison grids generated");
    } catch (error) {
      console.error(error);
      setError(error?.message || "Unable to generate comparison grids");
    } finally {
      setWorking(false);
    }
  };

  const displaySectionAComparisons = React.useMemo(() => {
    if (!isRollupAggregation) return sectionAComparisons;

    const rows = (sectionAByAssessmentData || [])
      .filter(({ sectionAs }) => (sectionAs || []).length > 0)
      .map(({ assessment_id: assessmentId, sectionAs }) => ({
        assessment: assessmentsById.get(String(assessmentId)),
        sectionA: rollupSectionA(sectionAs, assessmentId),
        sourceObservationCount: (sectionAs || []).length,
      }))
      .sort((a, b) => {
        const aDate = a.assessment?.started_on?.valueOf ? a.assessment.started_on.valueOf() : 0;
        const bDate = b.assessment?.started_on?.valueOf ? b.assessment.started_on.valueOf() : 0;
        return aDate - bDate;
      });

    return rows.length ? [{ observationRange: "Section A Summary", rows }] : [];
  }, [isRollupAggregation, sectionAComparisons, sectionAByAssessmentData, assessmentsById]);

  const displaySectionBComparisons = React.useMemo(() => {
    if (!isRollupAggregation) return sectionBComparisons;

    const rows = (sectionBByAssessmentData || [])
      .filter(({ sectionBs }) => (sectionBs || []).length > 0)
      .map(({ assessment_id: assessmentId, sectionBs }) => ({
        assessment: assessmentsById.get(String(assessmentId)),
        sectionB: {
          id: `section-b-summary-${assessmentId}`,
          assessment_id: assessmentId,
          observed_on: null,
        },
        behaviorValues: rollupSectionBValues(sectionBs, selectedSectionBDataMode),
        sourceObservationCount: (sectionBs || []).length,
      }))
      .sort((a, b) => {
        const aDate = a.assessment?.started_on?.valueOf ? a.assessment.started_on.valueOf() : 0;
        const bDate = b.assessment?.started_on?.valueOf ? b.assessment.started_on.valueOf() : 0;
        return aDate - bDate;
      });

    return rows.length ? [{ observationRange: "Section B Summary", rows }] : [];
  }, [
    isRollupAggregation,
    sectionBComparisons,
    sectionBByAssessmentData,
    assessmentsById,
    selectedSectionBDataMode,
  ]);

  return (
    <Container component="main" maxWidth={false}>
      <Header label={title} />

      <Paper variant="outlined" sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Box sx={{ width: "100%", maxWidth: "md", mx: "auto" }}>
            <Stack spacing={2}>
              <Typography variant="h6">Select assessments to compare</Typography>
              <Typography variant="body2" color="text.secondary">
                Choose one animal, then select multiple assessments from that animal.
              </Typography>

              <FormProvider {...methods}>
                <Form onSubmit={handleSubmit(onSubmit)}>
                  <Grid container spacing={2}>
                    <Grid xs={12}>
                        <FormDropDownFilter
                            name="animal_id"
                            label="Animal"
                            list={animalOptions}
                            enableTextFilter={true}
                            enableSelectFilter={true}
                            selectFilterList={speciesFilter}
                            required
                        />

                    </Grid>

                    <Grid xs={12}>
                      <FormMultiSelectChips
                        name="assessment_ids"
                        label="Assessments"
                        list={assessmentOptions}
                        enableSelectAll
                        required
                      />
                    </Grid>

                    <Grid xs={12}>
                      <FormRadioGroup
                        name="observation_aggregation"
                        label="Observation Aggregation"
                        list={OBSERVATION_AGGREGATION_OPTIONS}
                      />
                    </Grid>

                    <Grid xs={12}>
                      <FormRadioGroup
                        name="section_a_data_mode"
                        label="Section A Data"
                        list={SECTION_A_DATA_MODE_OPTIONS}
                      />
                    </Grid>

                    <Grid xs={12}>
                      <FormRadioGroup
                        name="section_b_data_mode"
                        label="Section B Data"
                        list={SECTION_B_DATA_MODE_OPTIONS}
                      />
                    </Grid>

                    <Grid xs={12}>
                      <FormButton working={working}>Compare</FormButton>
                    </Grid>
                  </Grid>
                </Form>
              </FormProvider>
            </Stack>
          </Box>

          {displaySectionAComparisons.length > 0 && (
            <>
              <Divider />

              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="h6">Section A</Typography>
                <IconButton onClick={() => setSectionAOpen(!sectionAOpen)} size="small">
                  {sectionAOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Stack>

              <Collapse in={sectionAOpen}>
                <Stack spacing={2}>
                {displaySectionAComparisons.map((rangeGroup) => {
                  const sectionAValueRows = rangeGroup.rows.map(({ assessment, sectionA }) => ({
                    assessment,
                    sectionA,
                  }));
                  const currentSectionADefinitions = sectionAValueDefinitions(selectedSectionADataMode);
                  const sectionASeriesLabels = sectionAValueRows.map(({ assessment, sectionA }) => (
                    assessmentSeriesLabel(assessment, sectionA.assessment_id)
                  ));

                  return (
                    <Paper key={rangeGroup.observationRange} variant="outlined" sx={{ p: 2 }}>
                      <Stack spacing={1.5}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {isRollupAggregation
                              ? rangeGroup.observationRange
                              : `Observation Range: ${rangeGroup.observationRange}`}
                          </Typography>
                          <IconButton
                            onClick={() => setSectionARangeOpen((prev) => ({
                              ...prev,
                              [rangeGroup.observationRange]: !prev[rangeGroup.observationRange],
                            }))}
                            size="small"
                          >
                            {(sectionARangeOpen[rangeGroup.observationRange] ?? true) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        </Stack>

                        <Collapse in={sectionARangeOpen[rangeGroup.observationRange] ?? true}>
                          <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={2}
                            alignItems={{ sm: "center" }}
                            sx={{ mb: 1 }}
                          >
                            <Typography variant="body2" color="text.secondary">
                              Radar size: {sectionARangeChartHeight[rangeGroup.observationRange] || DEFAULT_RADAR_HEIGHT}px
                            </Typography>
                            <Slider
                              size="small"
                              min={MIN_RADAR_HEIGHT}
                              max={MAX_RADAR_HEIGHT}
                              step={RADAR_HEIGHT_STEP}
                              value={sectionARangeChartHeight[rangeGroup.observationRange] || DEFAULT_RADAR_HEIGHT}
                              onChange={(_event, value) => {
                                const nextValue = Array.isArray(value) ? value[0] : value;
                                setSectionARangeChartHeight((prev) => ({
                                  ...prev,
                                  [rangeGroup.observationRange]: nextValue,
                                }));
                              }}
                              sx={{ maxWidth: 360, width: "100%" }}
                            />
                            <Button
                              size="small"
                              onClick={() => {
                                setSectionARangeChartHeight((prev) => ({
                                  ...prev,
                                  [rangeGroup.observationRange]: DEFAULT_RADAR_HEIGHT,
                                }));
                              }}
                            >
                              Reset
                            </Button>
                          </Stack>

                          <Box
                            sx={{
                              mb: 2,
                              height: sectionARangeChartHeight[rangeGroup.observationRange] || DEFAULT_RADAR_HEIGHT,
                            }}
                          >
                            <Radar
                              key={`section-a-${rangeGroup.observationRange}-${selectedSectionADataMode}-${sectionARangeChartHeight[rangeGroup.observationRange] || DEFAULT_RADAR_HEIGHT}`}
                              redraw
                              data={{
                                labels: currentSectionADefinitions.map((definition) => definition.label),
                                datasets: sectionAValueRows.map(({ assessment, sectionA }) => {
                                  const color = assessmentColor(assessment?.id || sectionA.assessment_id);
                                  const label = assessmentSeriesLabel(assessment, sectionA.assessment_id);
                                  return {
                                    label,
                                    data: currentSectionADefinitions.map((definition) => definition.getValue(sectionA)),
                                    borderColor: color,
                                    backgroundColor: toAlpha(color, 0.15),
                                    pointBackgroundColor: color,
                                    pointRadius: 2,
                                    borderWidth: 2,
                                    hidden: sectionARadarSeriesVisibility[rangeGroup.observationRange]?.[label] === false,
                                  };
                                }),
                              }}
                              options={{
                                maintainAspectRatio: false,
                                plugins: { legend: { position: "bottom" } },
                                scales: {
                                  r: {
                                    beginAtZero: true,
                                    suggestedMin: 0,
                                    suggestedMax: 10,
                                  },
                                },
                              }}
                            />
                          </Box>
                          <ChartSeriesControls
                            onActivateAll={() => setSectionARadarSeriesVisibility((prev) => ({
                              ...prev,
                              [rangeGroup.observationRange]: Object.fromEntries(
                                sectionASeriesLabels.map((label) => [label, true])
                              ),
                            }))}
                            onDeactivateAll={() => setSectionARadarSeriesVisibility((prev) => ({
                              ...prev,
                              [rangeGroup.observationRange]: Object.fromEntries(
                                sectionASeriesLabels.map((label) => [label, false])
                              ),
                            }))}
                          />

                          <TableContainer component={Box} sx={{ width: "100%", overflowX: "auto" }}>
                            <Table
                              size="small"
                              aria-label={`Section A comparison ${rangeGroup.observationRange}`}
                              sx={{ minWidth: "max-content" }}
                            >
                              <TableHead>
                                <TableRow>
                                  <TableCell>Score</TableCell>
                                  {sectionAValueRows.map(({ assessment, sectionA }) => (
                                    <TableCell key={sectionA.id} align="right">
                                      <Stack spacing={0.5}>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                          {assessment?.assessment_title
                                            || `Assessment ${assessment?.id || sectionA.assessment_id}`}
                                          <Box
                                            component="span"
                                            sx={{
                                              display: "inline-block",
                                              width: 10,
                                              height: 10,
                                              borderRadius: "2px",
                                              ml: 1,
                                              verticalAlign: "middle",
                                              bgcolor: toAlpha(
                                                assessmentColor(assessment?.id || sectionA.assessment_id),
                                                0.3
                                              ),
                                              border: "1px solid",
                                              borderColor: assessmentColor(assessment?.id || sectionA.assessment_id),
                                            }}
                                          />
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          By {usersById.get(String(assessment?.user_id))?.name || "Unknown user"}
                                        </Typography>
                                      </Stack>
                                    </TableCell>
                                  ))}
                                  <TableCell align="right">Average</TableCell>
                                  <TableCell align="right">Median</TableCell>
                                  <TableCell align="right">Std Dev</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                <TableRow>
                                  <TableCell>{isRollupAggregation ? "Observation Ranges" : "Observed On"}</TableCell>
                                  {rangeGroup.rows.map(({ sectionA, sourceObservationCount }) => (
                                    <TableCell key={`${sectionA.id}-observed-on`} align="right">
                                      {isRollupAggregation
                                        ? sourceObservationCount
                                        : (sectionA?.observed_on ? date(sectionA.observed_on) : "-")}
                                    </TableCell>
                                  ))}
                                  <TableCell align="right">-</TableCell>
                                  <TableCell align="right">-</TableCell>
                                  <TableCell align="right">-</TableCell>
                                </TableRow>
                                {currentSectionADefinitions.map((definition) => {
                                  const rowValues = sectionAValueRows.map(({ sectionA }) => definition.getValue(sectionA));
                                  const { avg, med, stdDev } = calculateStats(rowValues);

                                  return (
                                    <TableRow key={definition.key}>
                                      <TableCell>{definition.label}</TableCell>
                                      {sectionAValueRows.map(({ sectionA }) => {
                                        const value = definition.getValue(sectionA);
                                        const cellStyle = deviationStyle(value, avg, stdDev);
                                        return (
                                          <TableCell
                                            key={`${sectionA.id}-${definition.key}`}
                                            align="right"
                                            sx={cellStyle || undefined}
                                          >
                                            {value.toFixed(1)}
                                          </TableCell>
                                        );
                                      })}
                                      <TableCell align="right">{avg.toFixed(2)}</TableCell>
                                      <TableCell align="right">{med.toFixed(2)}</TableCell>
                                      <TableCell align="right">{stdDev.toFixed(2)}</TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Collapse>
                      </Stack>
                    </Paper>
                  );
                })}
                </Stack>
              </Collapse>
            </>
          )}

          {displaySectionBComparisons.length > 0 && (
            <>
              <Divider />

              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="h6">Section B</Typography>
                <IconButton onClick={() => setSectionBOpen(!sectionBOpen)} size="small">
                  {sectionBOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Stack>

              <Collapse in={sectionBOpen}>
                <Stack spacing={2}>
                {displaySectionBComparisons.map((rangeGroup) => {
                  const sectionBRowsWithValues = rangeGroup.rows.map(({ assessment, sectionB, behaviorValues }) => ({
                    assessment,
                    sectionB,
                    behaviorValues: behaviorValues || sectionBBehaviorValueMap(sectionB, selectedSectionBDataMode),
                  }));
                  const isPercentMode = selectedSectionBDataMode === SECTION_B_DATA_MODE_PERCENT;
                  const sectionBRadarMax = isPercentMode ? 100 : undefined;
                  const sectionBSeriesLabels = sectionBRowsWithValues.map(({ assessment, sectionB }) => (
                    assessmentSeriesLabel(assessment, sectionB.assessment_id)
                  ));

                  return (
                    <Paper key={rangeGroup.observationRange} variant="outlined" sx={{ p: 2 }}>
                      <Stack spacing={1.5}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {isRollupAggregation
                              ? rangeGroup.observationRange
                              : `Observation Range: ${rangeGroup.observationRange}`}
                          </Typography>
                          <IconButton
                            onClick={() => setSectionBRangeOpen((prev) => ({
                              ...prev,
                              [rangeGroup.observationRange]: !prev[rangeGroup.observationRange],
                            }))}
                            size="small"
                          >
                            {(sectionBRangeOpen[rangeGroup.observationRange] ?? true) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        </Stack>

                        <Collapse in={sectionBRangeOpen[rangeGroup.observationRange] ?? true}>
                          <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={2}
                            alignItems={{ sm: "center" }}
                            sx={{ mb: 1 }}
                          >
                            <Typography variant="body2" color="text.secondary">
                              Radar size: {sectionBRangeChartHeight[rangeGroup.observationRange] || DEFAULT_RADAR_HEIGHT}px
                            </Typography>
                            <Slider
                              size="small"
                              min={MIN_RADAR_HEIGHT}
                              max={MAX_RADAR_HEIGHT}
                              step={RADAR_HEIGHT_STEP}
                              value={sectionBRangeChartHeight[rangeGroup.observationRange] || DEFAULT_RADAR_HEIGHT}
                              onChange={(_event, value) => {
                                const nextValue = Array.isArray(value) ? value[0] : value;
                                setSectionBRangeChartHeight((prev) => ({
                                  ...prev,
                                  [rangeGroup.observationRange]: nextValue,
                                }));
                              }}
                              sx={{ maxWidth: 360, width: "100%" }}
                            />
                            <Button
                              size="small"
                              onClick={() => {
                                setSectionBRangeChartHeight((prev) => ({
                                  ...prev,
                                  [rangeGroup.observationRange]: DEFAULT_RADAR_HEIGHT,
                                }));
                              }}
                            >
                              Reset
                            </Button>
                          </Stack>

                          <Box
                            sx={{
                              mb: 2,
                              height: sectionBRangeChartHeight[rangeGroup.observationRange] || DEFAULT_RADAR_HEIGHT,
                            }}
                          >
                            <Radar
                              key={`section-b-${rangeGroup.observationRange}-${selectedSectionBDataMode}-${sectionBRangeChartHeight[rangeGroup.observationRange] || DEFAULT_RADAR_HEIGHT}`}
                              redraw
                              data={{
                                labels: sectionBBehaviors.map((behaviorDefinition) => behaviorDefinition.title),
                                datasets: sectionBRowsWithValues.map(({ assessment, sectionB, behaviorValues }) => {
                                  const color = assessmentColor(assessment?.id || sectionB.assessment_id);
                                  const label = assessmentSeriesLabel(assessment, sectionB.assessment_id);
                                  return {
                                    label,
                                    data: sectionBBehaviors.map((behaviorDefinition) => (
                                      behaviorValues[behaviorDefinition.behaviorKey] || 0
                                    )),
                                    borderColor: color,
                                    backgroundColor: toAlpha(color, 0.15),
                                    pointBackgroundColor: color,
                                    pointRadius: 2,
                                    borderWidth: 2,
                                    hidden: sectionBRadarSeriesVisibility[rangeGroup.observationRange]?.[label] === false,
                                  };
                                }),
                              }}
                              options={{
                                maintainAspectRatio: false,
                                plugins: { legend: { position: "bottom" } },
                                scales: {
                                  r: {
                                    beginAtZero: true,
                                    suggestedMin: 0,
                                    suggestedMax: sectionBRadarMax,
                                  },
                                },
                              }}
                            />
                          </Box>
                          <ChartSeriesControls
                            onActivateAll={() => setSectionBRadarSeriesVisibility((prev) => ({
                              ...prev,
                              [rangeGroup.observationRange]: Object.fromEntries(
                                sectionBSeriesLabels.map((label) => [label, true])
                              ),
                            }))}
                            onDeactivateAll={() => setSectionBRadarSeriesVisibility((prev) => ({
                              ...prev,
                              [rangeGroup.observationRange]: Object.fromEntries(
                                sectionBSeriesLabels.map((label) => [label, false])
                              ),
                            }))}
                          />

                          <TableContainer component={Box} sx={{ width: "100%", overflowX: "auto" }}>
                            <Table
                              size="small"
                              aria-label={`Section B comparison ${rangeGroup.observationRange}`}
                              sx={{ minWidth: "max-content" }}
                            >
                              <TableHead>
                                <TableRow>
                                  <TableCell>Behavior</TableCell>
                                  {sectionBRowsWithValues.map(({ assessment, sectionB }) => (
                                    <TableCell key={sectionB.id} align="right">
                                      <Stack spacing={0.5}>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                          {assessment?.assessment_title
                                            || `Assessment ${assessment?.id || sectionB.assessment_id}`}
                                          <Box
                                            component="span"
                                            sx={{
                                              display: "inline-block",
                                              width: 10,
                                              height: 10,
                                              borderRadius: "2px",
                                              ml: 1,
                                              verticalAlign: "middle",
                                              bgcolor: toAlpha(
                                                assessmentColor(assessment?.id || sectionB.assessment_id),
                                                0.3
                                              ),
                                              border: "1px solid",
                                              borderColor: assessmentColor(assessment?.id || sectionB.assessment_id),
                                            }}
                                          />
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          By {usersById.get(String(assessment?.user_id))?.name || "Unknown user"}
                                        </Typography>
                                      </Stack>
                                    </TableCell>
                                  ))}
                                  <TableCell align="right">Average</TableCell>
                                  <TableCell align="right">Median</TableCell>
                                  <TableCell align="right">Std Dev</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                <TableRow>
                                  <TableCell>{isRollupAggregation ? "Observation Ranges" : "Observed On"}</TableCell>
                                  {rangeGroup.rows.map(({ sectionB, sourceObservationCount }) => (
                                    <TableCell key={`${sectionB.id}-observed-on`} align="right">
                                      {isRollupAggregation
                                        ? sourceObservationCount
                                        : (sectionB?.observed_on ? date(sectionB.observed_on) : "-")}
                                    </TableCell>
                                  ))}
                                  <TableCell align="right">-</TableCell>
                                  <TableCell align="right">-</TableCell>
                                  <TableCell align="right">-</TableCell>
                                </TableRow>
                                {sectionBBehaviors.map((behaviorDefinition) => {
                                  const rowValues = sectionBRowsWithValues.map(({ behaviorValues }) => (
                                    behaviorValues[behaviorDefinition.behaviorKey] || 0
                                  ));
                                  const { avg, med, stdDev } = calculateStats(rowValues);

                                  return (
                                    <TableRow key={behaviorDefinition.behaviorKey}>
                                      <TableCell>{behaviorDefinition.title}</TableCell>
                                      {sectionBRowsWithValues.map(({ sectionB, behaviorValues }) => {
                                        const value = behaviorValues[behaviorDefinition.behaviorKey] || 0;
                                        const cellStyle = deviationStyle(value, avg, stdDev);
                                        return (
                                          <TableCell
                                            key={`${sectionB.id}-${behaviorDefinition.behaviorKey}`}
                                            align="right"
                                            sx={cellStyle || undefined}
                                          >
                                            {formatSectionBValue(value, selectedSectionBDataMode)}
                                          </TableCell>
                                        );
                                      })}
                                      <TableCell align="right">{avg.toFixed(2)}</TableCell>
                                      <TableCell align="right">{med.toFixed(2)}</TableCell>
                                      <TableCell align="right">{stdDev.toFixed(2)}</TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Collapse>
                      </Stack>
                    </Paper>
                  );
                })}
                </Stack>
              </Collapse>
            </>
          )}
        </Stack>
      </Paper>
    </Container>
  );
}
